import GenericAction from '@/components/actions/GenericAction.tsx'
import AActionContentGenericLayout from '@/components/actions/components/AActionContentGenericLayout.jsx'
import AActionContentDescription from '@/components/actions/components/AActionContentDescription.jsx'
import { FoldVertical, ScanBarcode, UnfoldVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useEffect, useRef, useState } from 'react'

type Product = {
  name: string
  creator: string
  description: string
  price: number
  list_price: number
  rating?: string
  sizes?: string[]
  colors?: string[]
  category?: string
  product_sku?: string
  product_id?: string
  other_details?: string
}

type ActionOutput = {
  status: string
  content: {
    products: Product[]
  }
  message: string
}

type Action = {
  outputs: ActionOutput[]
  args?: {
    comment?: string
  }
}

type ComposeProductsActionProps = {
  action: Action
  isLast?: boolean
  isInteractive?: boolean
}

const Dot = () => <span className="text-xs leading-none text-stone-500">·</span>

const formatCategory = (category: string): string => {
  if (category.includes('-')) {
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  return category
}

export default function ComposeProductsAction({
  action,
  isLast = false,
  isInteractive = false,
}: ComposeProductsActionProps) {
  const [showAll, setShowAll] = useState(false)
  const gridRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const output = action.outputs.find(o => o.status === 'success')
  const products = output?.content.products || []
  const displayedProducts = showAll ? products : products.slice(0, 4)

  const handleToggle = () => {
    setShowAll(!showAll)
  }

  useEffect(() => {
    if (showAll) {
      setTimeout(() => {
        buttonRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } else {
      gridRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [showAll])

  const descriptionContent = [
    <div key="description" className="mb-2 whitespace-pre-line text-sm leading-tight">
      {output?.message}
    </div>,
  ]

  return (
    <GenericAction
      title={action?.args?.comment ?? 'Compose products'}
      action={action}
      actionIcon={ScanBarcode}
      isLast={isLast}
      isInteractive={isInteractive}
    >
      <AActionContentGenericLayout>
        <AActionContentDescription content={descriptionContent} />
        <div ref={gridRef} className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {displayedProducts.map((product: Product, index: number) => (
            <div
              key={index}
              className="flex flex-col items-center justify-center overflow-hidden rounded-md bg-stone-50 outline outline-1 outline-offset-[-1px] outline-stone-200"
            >
              <div className="flex h-full flex-col space-y-3 border-t border-stone-200 px-4 pb-3 pt-4">
                <div className="flex flex-1 flex-col space-y-3">
                  <div className="space-y-0.5">
                    <div className="flex items-center space-x-1 text-xs text-stone-500">
                      <span>{product.creator}</span>
                      <Dot />
                      <span>{formatCategory(product.category)}</span>
                    </div>
                    <div className="text-base font-medium text-stone-900">{product.name}</div>
                    <div className="flex items-center space-x-1 text-xs text-stone-500">
                      <span>${product.price}</span>
                      <Dot />
                      <span>{product.sizes?.length || 0} sizes</span>
                      <Dot />
                      <span>{product.colors?.length || 0} colors</span>
                    </div>
                  </div>
                  <div className="h-0 outline outline-1 outline-offset-[-0.5px] outline-stone-200" />
                  <div className="text-sm text-stone-500">{product.description}</div>
                </div>
                <div className="mt-auto flex items-center space-x-1 text-xs text-stone-500">
                  {product.product_sku && (
                    <>
                      <span title="SKU">{product.product_sku}</span>
                      <Dot />
                    </>
                  )}
                  {product.product_id && (
                    <>
                      <span title="Product id">{product.product_id}</span>
                      <Dot />
                    </>
                  )}
                  {product.rating && <span title="Rating">{product.rating}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
        {products.length > 4 && (
          <Button
            ref={buttonRef}
            className="flex h-8 w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-stone-100 px-3 py-1.5 text-stone-900"
            variant="secondary"
            onClick={handleToggle}
          >
            {showAll ? (
              <FoldVertical className="relative size-4" />
            ) : (
              <UnfoldVertical className="relative size-4" />
            )}

            <div className="font-['Inter'] text-sm font-medium leading-tight ">
              {showAll ? `Show less products` : `Show all products`}
            </div>
          </Button>
        )}
      </AActionContentGenericLayout>
    </GenericAction>
  )
}
